"use client";

import { c as _c } from "react/compiler-runtime";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { Sidebar, SidebarBody } from "@/components/ui/sidebar";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NAVIGATION_LINKS } from "@/config/constants";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useOrganizations } from "@/hooks/useOrganizations";
import { usePlanUsage, type PlanUsageMetrics } from "@/hooks/usePlanUsage";
import { useSubscription } from "@/hooks/useSubscription";
import { useSyncTailwindDarkClass } from "@/hooks/useSyncTailwindDarkClass";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/routes";
import { useCommandMenuStore } from "@/stores/commandMenuStore";
import { useSupportStore } from "@/stores/supportStore";
import { getTierDisplayName } from "@/types/billing";
import type { SubscriptionData } from "@/types/subscription";
import { IconArrowBoxRight, IconMagnifyingGlass, IconMoonStar, IconNewspaper2, IconPlusLarge } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { useColorMode } from "@chakra-ui/react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { IconAddMagicOutline18, IconComputerOutline18, IconSitemap5Outline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import useMeasure from "react-use-measure";
import { isEmbeddedMasquerade } from "@/lib/masquerade";
import SidebarLogo from "./SidebarLogo";
import UserOrgSwitcher from "./UserOrgSwitcher";
function SidebarPlanStat(t0) {
  const $ = _c(36);
  const {
    icon,
    title,
    detail,
    progress: t1
  } = t0;
  const progress = t1 === undefined ? 0 : t1;
  const circumference = 2 * Math.PI * 16;
  const strokeDashoffset = circumference * (1 - Math.min(progress, 1));
  const idSuffix = title.replace(/\s+/g, "-").toLowerCase();
  const filterId = `glow-${idSuffix}`;
  const clipId = `clip-${idSuffix}`;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="shadow-ds-card-halo absolute inset-0 rounded-full" />;
    t3 = <div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 shadow-ds-debossed absolute inset-0 rounded-full" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <circle cx="20" cy="20" r="20" />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== clipId) {
    t5 = <clipPath id={clipId}>{t4}</clipPath>;
    $[3] = clipId;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />;
    t7 = <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== filterId) {
    t8 = <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">{t6}{t7}</filter>;
    $[7] = filterId;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t5 || $[10] !== t8) {
    t9 = <defs>{t5}{t8}</defs>;
    $[9] = t5;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = `url(#${clipId})`;
  const t11 = progress > 0 ? `url(#${filterId})` : undefined;
  let t12;
  if ($[12] !== strokeDashoffset || $[13] !== t11) {
    t12 = <circle cx="20" cy="20" r={16} strokeWidth="3" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} filter={t11} className="stroke-ds-brand-500 transition-[stroke-dashoffset] duration-500 ease-out" />;
    $[12] = strokeDashoffset;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t12) {
    t13 = <g clipPath={t10}>{t12}</g>;
    $[15] = t10;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t13 || $[19] !== t9) {
    t14 = <svg className="absolute left-0 right-0 top-[-1px] size-full -rotate-90" viewBox="0 0 40 40" fill="none">{t9}{t13}</svg>;
    $[18] = t13;
    $[19] = t9;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== icon) {
    t15 = <span className="bg-ds-surface-card shadow-ds-input-inset text-ds-text-disabled dark:text-ds-text-muted absolute left-1/2 top-1/2 z-10 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full">{icon}</span>;
    $[21] = icon;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14 || $[24] !== t15) {
    t16 = <div className="relative size-10 shrink-0">{t2}{t3}{t14}{t15}</div>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== title) {
    t17 = <p className="leading-label text-ds-text-primary mb-1 text-sm font-medium">{title}</p>;
    $[26] = title;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== detail) {
    t18 = <p className="text-2xs leading-label text-ds-text-muted">{detail}</p>;
    $[28] = detail;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17 || $[31] !== t18) {
    t19 = <div className="min-w-0">{t17}{t18}</div>;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t16 || $[34] !== t19) {
    t20 = <div className="flex items-center gap-3">{t16}{t19}</div>;
    $[33] = t16;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
}
function formatCompactSidebarValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
}
function computeSidebarGaugeProgress(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(used / total, 1);
}
function SidebarPlanStats(t0) {
  const $ = _c(25);
  const {
    metrics
  } = t0;
  const cuUsed = metrics?.cu.used ?? 0;
  const cuCeiling = metrics?.cu.ceiling ?? 0;
  const aiUsed = metrics?.ai.used ?? 0;
  const aiCeiling = metrics?.ai.ceiling ?? 0;
  const wfActive = metrics?.workflows.active ?? 0;
  const wfMax = metrics?.workflows.max ?? 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconComputerOutline18 className="size-[18px]" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `${formatCompactSidebarValue(cuUsed)} of ${formatCompactSidebarValue(cuCeiling)} CU`;
  let t3;
  if ($[1] !== cuCeiling || $[2] !== cuUsed) {
    t3 = computeSidebarGaugeProgress(cuUsed, cuCeiling);
    $[1] = cuCeiling;
    $[2] = cuUsed;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <SidebarPlanStat icon={t1} title="CU Balance" detail={t2} progress={t3} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconAddMagicOutline18 className="size-[18px]" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `${formatCompactSidebarValue(aiUsed)} of ${formatCompactSidebarValue(aiCeiling)} credits`;
  let t7;
  if ($[8] !== aiCeiling || $[9] !== aiUsed) {
    t7 = computeSidebarGaugeProgress(aiUsed, aiCeiling);
    $[8] = aiCeiling;
    $[9] = aiUsed;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <SidebarPlanStat icon={t5} title="AI Credits" detail={t6} progress={t7} />;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconSitemap5Outline18 className="size-[18px]" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = wfMax === 0 ? `${wfActive} active workflows` : `${wfActive} of ${wfMax} slots`;
  let t11;
  if ($[15] !== wfActive || $[16] !== wfMax) {
    t11 = computeSidebarGaugeProgress(wfActive, wfMax);
    $[15] = wfActive;
    $[16] = wfMax;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = <SidebarPlanStat icon={t9} title="Workflow Slots" detail={t10} progress={t11} />;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t4 || $[23] !== t8) {
    t13 = <>{t4}{t8}{t12}</>;
    $[21] = t12;
    $[22] = t4;
    $[23] = t8;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
}
function SidebarPlanCard(t0) {
  const $ = _c(14);
  const {
    data,
    metrics,
    loading
  } = t0;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 mb-3 h-4 w-24 animate-pulse rounded" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="border-ds-border-default dark:bg-ds-neutral-900 rounded-xl border bg-zinc-50 p-3">{t1}<div className="space-y-3">{Array.from({
            length: 3
          }).map(_temp)}</div></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t1 = data?.subscription.tier ?? "free";
  let t2;
  if ($[2] !== t1) {
    t2 = getTierDisplayName(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tierLabel = t2;
  let t3;
  if ($[4] !== tierLabel) {
    t3 = <p className="leading-label text-ds-text-primary text-sm font-medium">{tierLabel} Plan</p>;
    $[4] = tierLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Link href="/billing" className="leading-label text-ds-brand-500 hover:text-ds-brand-600 text-sm font-medium transition-colors">Manage</Link>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <div className="mb-3 flex items-center justify-between gap-3">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== metrics) {
    t6 = <div className="space-y-3"><SidebarPlanStats metrics={metrics} /></div>;
    $[9] = metrics;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="bg-ds-surface-primary shadow-ds-soft border-ds-border-default rounded-xl border p-3">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp(_, index) {
  return <div key={index} className="bg-ds-neutral-200/80 dark:bg-ds-neutral-800 h-10 animate-pulse rounded" />;
}
export default function MainSidebar(t0) {
  const $ = _c(170);
  const {
    className,
    onClose,
    collapsed,
    onToggleCollapse,
    bottomSlot
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    selectedOrgId
  } = useOrganizations();
  const orgId = collapsed ? undefined : selectedOrgId ?? undefined;
  const subscriptionQuery = useSubscription(orgId);
  const planUsage = usePlanUsage(orgId);
  const setCommandMenuOpen = useCommandMenuStore(_temp2);
  const {
    isAuthenticated,
    isAuthenticating
  } = useAppAuth();
  const {
    colorMode,
    setColorMode
  } = useColorMode();
  const [isTallViewport, setIsTallViewport] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const mql = window.matchMedia("(min-height: 920px)");
      const update = () => setIsTallViewport(mql.matches);
      update();
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [planPanelHovered, setPlanPanelHovered] = useState(false);
  const [measureRef, t3] = useMeasure();
  const {
    height: planContentHeight
  } = t3;
  const hoverTimeoutRef = useRef(null);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const clearPlanTimeout = t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => clearPlanTimeout();
    t6 = [clearPlanTimeout];
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      clearPlanTimeout();
      hoverTimeoutRef.current = setTimeout(() => setPlanPanelHovered(true), 150);
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handlePlanHoverEnter = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      clearPlanTimeout();
      setPlanPanelHovered(false);
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const handlePlanHoverLeave = t8;
  const openSupportModal = useSupportStore(_temp3);
  const isDarkMode = colorMode === "dark";
  useSyncTailwindDarkClass();
  let t9;
  if ($[7] !== isDarkMode || $[8] !== setColorMode) {
    t9 = () => {
      setColorMode(isDarkMode ? "light" : "dark");
    };
    $[7] = isDarkMode;
    $[8] = setColorMode;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const toggleTheme = t9;
  let t10;
  if ($[10] !== onClose || $[11] !== router) {
    t10 = () => {
      router.push(`/workflows/edit?r=${crypto.randomUUID()}`);
      onClose?.();
    };
    $[10] = onClose;
    $[11] = router;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const createWorkflow = t10;
  let t11;
  if ($[13] !== pathname) {
    t11 = href => {
      if (href === "/dashboard") {
        return pathname === "/dashboard";
      }
      if (href === "/workflows") {
        return pathname === "/workflows";
      }
      return pathname.startsWith(href);
    };
    $[13] = pathname;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const isActiveRoute = t11;
  const iconFor = _temp4;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = iconFor("/dashboard");
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== isActiveRoute) {
    t13 = isActiveRoute("/dashboard");
    $[16] = isActiveRoute;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t13) {
    t14 = {
      label: "Home",
      href: "/dashboard",
      icon: t12,
      dataGuide: "sidebar-home",
      active: t13
    };
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = iconFor("/templates");
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== isActiveRoute) {
    t16 = isActiveRoute("/templates");
    $[21] = isActiveRoute;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t16) {
    t17 = {
      label: "Templates",
      href: "/templates",
      icon: t15,
      dataGuide: "sidebar-templates",
      active: t16
    };
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = iconFor("/connectors");
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== isActiveRoute) {
    t19 = isActiveRoute("/connectors");
    $[26] = isActiveRoute;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t19) {
    t20 = {
      label: "Connectors",
      href: "/connectors",
      icon: t18,
      dataGuide: "sidebar-connectors",
      active: t19
    };
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t14 || $[31] !== t17 || $[32] !== t20) {
    t21 = [t14, t17, t20];
    $[30] = t14;
    $[31] = t17;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const mainMenuItems = t21;
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = iconFor("/workflows");
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== isActiveRoute) {
    t23 = isActiveRoute("/workflows");
    $[35] = isActiveRoute;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t23) {
    t24 = {
      label: "Workflows",
      href: "/workflows",
      icon: t22,
      dataGuide: "sidebar-workflows",
      active: t23
    };
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = iconFor("/workflows/history");
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== isActiveRoute) {
    t26 = isActiveRoute("/workflows/history");
    $[40] = isActiveRoute;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== t26) {
    t27 = {
      label: "Run History",
      href: "/workflows/history",
      icon: t25,
      dataGuide: "sidebar-run-history",
      active: t26
    };
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = iconFor("/databases");
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== isActiveRoute) {
    t29 = isActiveRoute("/databases");
    $[45] = isActiveRoute;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t29) {
    t30 = {
      label: "Databases",
      href: "/databases",
      icon: t28,
      dataGuide: "sidebar-databases",
      active: t29
    };
    $[47] = t29;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = iconFor("/wallet-management");
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== isActiveRoute) {
    t32 = isActiveRoute("/wallet-management");
    $[50] = isActiveRoute;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== t32) {
    t33 = {
      label: "Wallet Management",
      href: "/wallet-management",
      icon: t31,
      dataGuide: "sidebar-wallet-management",
      active: t32
    };
    $[52] = t32;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== t24 || $[55] !== t27 || $[56] !== t30 || $[57] !== t33) {
    t34 = [t24, t27, t30, t33];
    $[54] = t24;
    $[55] = t27;
    $[56] = t30;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  const workspaceItems = t34;
  if (!collapsed) {
    const t35 = onClose || collapsed !== undefined ? "" : "fixed left-0 top-0";
    let t36;
    if ($[59] !== className || $[60] !== t35) {
      t36 = cn("bg-ds-surface-secondary px-0 py-0 dark:bg-zinc-950", t35, className);
      $[59] = className;
      $[60] = t35;
      $[61] = t36;
    } else {
      t36 = $[61];
    }
    let t37;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <SidebarLogo />;
      $[62] = t37;
    } else {
      t37 = $[62];
    }
    let t38;
    if ($[63] !== isAuthenticated) {
      t38 = isAuthenticated && <NotificationBell />;
      $[63] = isAuthenticated;
      $[64] = t38;
    } else {
      t38 = $[64];
    }
    let t39;
    if ($[65] !== t38) {
      t39 = <div className="mb-4 flex shrink-0 items-center justify-between px-1 py-2">{t37}{t38}</div>;
      $[65] = t38;
      $[66] = t39;
    } else {
      t39 = $[66];
    }
    let t40;
    if ($[67] !== setCommandMenuOpen) {
      t40 = () => setCommandMenuOpen(true);
      $[67] = setCommandMenuOpen;
      $[68] = t40;
    } else {
      t40 = $[68];
    }
    let t41;
    let t42;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <div className="text-ds-text-disabled flex items-center gap-2"><IconMagnifyingGlass className="size-3.5" /><span className="leading-label text-ds-text-disabled text-sm font-normal">Search...</span></div>;
      t42 = <kbd className="ds-soft-kbd px-2 py-1">⌘ K</kbd>;
      $[69] = t41;
      $[70] = t42;
    } else {
      t41 = $[69];
      t42 = $[70];
    }
    let t43;
    if ($[71] !== t40) {
      t43 = <button type="button" data-voice-target="command-menu-entry" onClick={t40} className="ds-soft-input hover:bg-ds-surface-hover mb-3 flex h-10 w-full shrink-0 items-center justify-between pl-[10px] pr-[8px] transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out active:scale-[0.98]">{t41}{t42}</button>;
      $[71] = t40;
      $[72] = t43;
    } else {
      t43 = $[72];
    }
    let t44;
    let t45;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t44 = <IconPlusLarge className="size-3.5" />;
      t45 = <span className="whitespace-nowrap">Create New Workflow</span>;
      $[73] = t44;
      $[74] = t45;
    } else {
      t44 = $[73];
      t45 = $[74];
    }
    let t46;
    if ($[75] !== createWorkflow) {
      t46 = <button type="button" onClick={createWorkflow} className="leading-label shadow-ds-brand-glossy group mb-4 flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-[linear-gradient(180deg,var(--ds-brand-500),var(--ds-brand-600))] px-4 text-sm font-medium text-white transition-[filter,transform,box-shadow] duration-150 ease-out hover:brightness-[1.04] active:scale-[0.96]">{t44}{t45}</button>;
      $[75] = createWorkflow;
      $[76] = t46;
    } else {
      t46 = $[76];
    }
    let t47;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <p className="text-ds-text-disabled text-3xs mb-1 px-3 font-medium uppercase tracking-wide">Main Menu</p>;
      $[77] = t47;
    } else {
      t47 = $[77];
    }
    let t48;
    if ($[78] !== mainMenuItems || $[79] !== onClose) {
      t48 = <div className="shrink-0">{t47}{mainMenuItems.map(item => <SidebarNavItem key={item.label} icon={item.icon} label={item.label} href={item.href} active={item.active} onClick={onClose} dataGuide={item.dataGuide} layoutId="sidebar-main-active" />)}</div>;
      $[78] = mainMenuItems;
      $[79] = onClose;
      $[80] = t48;
    } else {
      t48 = $[80];
    }
    let t49;
    if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
      t49 = <p className="text-ds-text-disabled text-3xs mb-1 shrink-0 px-3 font-medium uppercase tracking-wide">Workspace</p>;
      $[81] = t49;
    } else {
      t49 = $[81];
    }
    let t50;
    if ($[82] !== onClose || $[83] !== workspaceItems) {
      t50 = <div className="border-ds-border-default mt-3 flex min-h-0 flex-1 flex-col border-t pt-3">{t49}<div className="scrollbar-subtle min-h-0 flex-1 space-y-0.5 overflow-y-auto">{workspaceItems.map(item_0 => <SidebarNavItem key={item_0.label} icon={item_0.icon} label={item_0.label} href={item_0.href} active={item_0.active} onClick={onClose} dataGuide={item_0.dataGuide} layoutId="sidebar-workspace-active" />)}</div></div>;
      $[82] = onClose;
      $[83] = workspaceItems;
      $[84] = t50;
    } else {
      t50 = $[84];
    }
    let t51;
    if ($[85] !== t48 || $[86] !== t50) {
      t51 = <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{t48}{t50}</div>;
      $[85] = t48;
      $[86] = t50;
      $[87] = t51;
    } else {
      t51 = $[87];
    }
    let t52;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
      t52 = <IconNewspaper2 className="size-5" />;
      $[88] = t52;
    } else {
      t52 = $[88];
    }
    let t53;
    if ($[89] !== openSupportModal) {
      t53 = <SidebarNavItem icon={t52} label="Guides & Help" onClick={() => openSupportModal("guides")} />;
      $[89] = openSupportModal;
      $[90] = t53;
    } else {
      t53 = $[90];
    }
    let t54;
    if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <IconMoonStar className="size-5" />;
      $[91] = t54;
    } else {
      t54 = $[91];
    }
    let t55;
    if ($[92] !== isDarkMode || $[93] !== toggleTheme) {
      t55 = <SidebarNavItem icon={t54} label="Dark mode" trailing={<Switch size="sm" checked={isDarkMode} onCheckedChange={toggleTheme} />} />;
      $[92] = isDarkMode;
      $[93] = toggleTheme;
      $[94] = t55;
    } else {
      t55 = $[94];
    }
    let t56;
    if ($[95] !== t53 || $[96] !== t55) {
      t56 = <div className="space-y-0.5">{t53}{t55}</div>;
      $[95] = t53;
      $[96] = t55;
      $[97] = t56;
    } else {
      t56 = $[97];
    }
    let t57;
    if ($[98] !== isAuthenticated || $[99] !== isTallViewport || $[100] !== measureRef || $[101] !== planContentHeight || $[102] !== planPanelHovered || $[103] !== planUsage || $[104] !== subscriptionQuery) {
      t57 = isAuthenticated && !isEmbeddedMasquerade() ? isTallViewport ? <SidebarPlanCard data={subscriptionQuery.data} metrics={planUsage} loading={subscriptionQuery.isLoading} /> : <div onMouseEnter={handlePlanHoverEnter} onMouseLeave={handlePlanHoverLeave} className="bg-ds-surface-primary shadow-ds-soft border-ds-border-default rounded-xl border p-3"><div className="flex items-center justify-between gap-3"><p className="leading-label text-ds-text-primary text-sm font-medium">{getTierDisplayName(subscriptionQuery.data?.subscription.tier ?? "free")} Plan</p><Link href="/billing" className="leading-label text-ds-brand-500 hover:text-ds-brand-600 text-sm font-medium transition-colors">Manage</Link></div><AnimatePresence>{planPanelHovered && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: planContentHeight,
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            type: "spring",
            damping: 25,
            stiffness: 300
          }} className="overflow-hidden"><div ref={measureRef}><div className="space-y-3 pt-3"><SidebarPlanStats metrics={planUsage} /></div></div></motion.div>}</AnimatePresence></div> : null;
      $[98] = isAuthenticated;
      $[99] = isTallViewport;
      $[100] = measureRef;
      $[101] = planContentHeight;
      $[102] = planPanelHovered;
      $[103] = planUsage;
      $[104] = subscriptionQuery;
      $[105] = t57;
    } else {
      t57 = $[105];
    }
    let t58;
    if ($[106] !== isAuthenticated || $[107] !== isAuthenticating) {
      t58 = <div className="pt-1">{isAuthenticating ? <div className="border-ds-border bg-ds-surface-primary shadow-ds-card-halo dark:border-ds-neutral-800 rounded-lg border p-2"><div className="bg-ds-surface-secondary dark:bg-ds-neutral-800 h-4 w-20 animate-pulse rounded" /></div> : isAuthenticated ? <UserOrgSwitcher /> : <Link href={ROUTES.LOGIN} className="border-ds-border bg-ds-surface-primary text-ds-text-tertiary shadow-ds-card-halo hover:bg-ds-surface-secondary dark:border-ds-neutral-800 flex w-full items-center gap-3 rounded-lg border p-2 text-sm font-normal leading-tight transition-colors dark:text-zinc-200 dark:hover:bg-zinc-950"><span className="shadow-ds-soft border-ds-border-default text-ds-text-disabled bg-ds-surface-card flex size-9 items-center justify-center rounded-full border"><IconArrowBoxRight className="h-4 w-4" /></span><span>Sign in</span></Link>}</div>;
      $[106] = isAuthenticated;
      $[107] = isAuthenticating;
      $[108] = t58;
    } else {
      t58 = $[108];
    }
    let t59;
    if ($[109] !== t56 || $[110] !== t57 || $[111] !== t58) {
      t59 = <div className="mt-4 space-y-3 pt-3">{t56}{t57}{t58}</div>;
      $[109] = t56;
      $[110] = t57;
      $[111] = t58;
      $[112] = t59;
    } else {
      t59 = $[112];
    }
    let t60;
    if ($[113] !== onToggleCollapse) {
      t60 = onToggleCollapse && <button type="button" onClick={onToggleCollapse} aria-label="Collapse sidebar" aria-expanded={true} className="absolute -right-6 top-14 z-50 flex h-8 w-5 items-center justify-center rounded-r-md bg-zinc-100 text-zinc-500 transition-colors hover:bg-zinc-300 hover:text-zinc-700 dark:bg-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-600" title="Collapse sidebar"><IconXmarkOutline18 className="size-3" /></button>;
      $[113] = onToggleCollapse;
      $[114] = t60;
    } else {
      t60 = $[114];
    }
    let t61;
    if ($[115] !== t39 || $[116] !== t43 || $[117] !== t46 || $[118] !== t51 || $[119] !== t59 || $[120] !== t60) {
      t61 = <div className="relative flex h-full flex-col justify-between px-3 py-3">{t39}{t43}{t46}{t51}{t59}{t60}</div>;
      $[115] = t39;
      $[116] = t43;
      $[117] = t46;
      $[118] = t51;
      $[119] = t59;
      $[120] = t60;
      $[121] = t61;
    } else {
      t61 = $[121];
    }
    let t62;
    if ($[122] !== t36 || $[123] !== t61) {
      t62 = <Sidebar><SidebarBody className={t36}>{t61}</SidebarBody></Sidebar>;
      $[122] = t36;
      $[123] = t61;
      $[124] = t62;
    } else {
      t62 = $[124];
    }
    return t62;
  }
  let t35;
  if ($[125] !== className) {
    t35 = cn("bg-transparent px-0 py-0", className);
    $[125] = className;
    $[126] = t35;
  } else {
    t35 = $[126];
  }
  let t36;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Link href="/dashboard" prefetch={false} className="mb-3 flex w-full items-center justify-center p-1"><Image src="https://cdn.b3.fun/b3os-logomark-embossed.png" alt="B3OS" width={32} height={32} quality={100} unoptimized={true} className="size-8 object-contain" /></Link>;
    $[127] = t36;
  } else {
    t36 = $[127];
  }
  let t37;
  if ($[128] !== setCommandMenuOpen) {
    t37 = () => setCommandMenuOpen(true);
    $[128] = setCommandMenuOpen;
    $[129] = t37;
  } else {
    t37 = $[129];
  }
  let t38;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <IconMagnifyingGlass className="size-[18px]" />;
    $[130] = t38;
  } else {
    t38 = $[130];
  }
  let t39;
  if ($[131] !== t37) {
    t39 = <TooltipTrigger asChild={true}><button type="button" data-voice-target="command-menu-entry" onClick={t37} className="flex w-full items-center justify-center rounded-lg bg-zinc-200 p-2 text-zinc-500 transition-colors hover:bg-zinc-300/70 hover:text-zinc-700 dark:bg-zinc-800/30 dark:text-zinc-300 dark:hover:bg-zinc-800/50">{t38}</button></TooltipTrigger>;
    $[131] = t37;
    $[132] = t39;
  } else {
    t39 = $[132];
  }
  let t40;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <TooltipContent side="right" sideOffset={8}>Search (⌘ K)</TooltipContent>;
    $[133] = t40;
  } else {
    t40 = $[133];
  }
  let t41;
  if ($[134] !== t39) {
    t41 = <Tooltip>{t39}{t40}</Tooltip>;
    $[134] = t39;
    $[135] = t41;
  } else {
    t41 = $[135];
  }
  let t42;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <IconPlusLarge className="size-4" />;
    $[136] = t42;
  } else {
    t42 = $[136];
  }
  let t43;
  if ($[137] !== createWorkflow) {
    t43 = <TooltipTrigger asChild={true}><button type="button" onClick={createWorkflow} className="shadow-ds-brand-cta flex w-full shrink-0 items-center justify-center rounded-md bg-[linear-gradient(180deg,var(--ds-brand-500),var(--ds-brand-600))] p-2 text-white transition-[filter,transform,box-shadow] duration-150 ease-out hover:brightness-[1.04] active:scale-[0.96]">{t42}</button></TooltipTrigger>;
    $[137] = createWorkflow;
    $[138] = t43;
  } else {
    t43 = $[138];
  }
  let t44;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <TooltipContent side="right" sideOffset={8}>Create New Workflow</TooltipContent>;
    $[139] = t44;
  } else {
    t44 = $[139];
  }
  let t45;
  if ($[140] !== t43) {
    t45 = <Tooltip>{t43}{t44}</Tooltip>;
    $[140] = t43;
    $[141] = t45;
  } else {
    t45 = $[141];
  }
  let t46;
  if ($[142] !== t41 || $[143] !== t45) {
    t46 = <div className="mb-2 space-y-2">{t41}{t45}</div>;
    $[142] = t41;
    $[143] = t45;
    $[144] = t46;
  } else {
    t46 = $[144];
  }
  let t47;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <p className="text-4xs mb-1 mt-2 text-center font-semibold uppercase tracking-widest text-zinc-400">Menu</p>;
    $[145] = t47;
  } else {
    t47 = $[145];
  }
  let t48;
  if ($[146] !== mainMenuItems || $[147] !== onClose) {
    t48 = <div className="rounded-lg bg-zinc-200 p-1 dark:bg-zinc-800/30">{mainMenuItems.map(item_1 => <SidebarNavItem key={item_1.label} icon={item_1.icon} label={item_1.label} href={item_1.href} active={item_1.active} onClick={onClose} dataGuide={item_1.dataGuide} layoutId="sidebar-main-active" collapsed={true} />)}</div>;
    $[146] = mainMenuItems;
    $[147] = onClose;
    $[148] = t48;
  } else {
    t48 = $[148];
  }
  let t49;
  let t50;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <hr className="mx-1 my-3 border-zinc-300 dark:border-zinc-700" />;
    t50 = <p className="text-4xs mb-1 text-center font-semibold uppercase tracking-widest text-zinc-400">W/S</p>;
    $[149] = t49;
    $[150] = t50;
  } else {
    t49 = $[149];
    t50 = $[150];
  }
  let t51;
  if ($[151] !== onClose || $[152] !== workspaceItems) {
    t51 = <div className="rounded-lg bg-zinc-200 p-1 dark:bg-zinc-800/30">{workspaceItems.map(item_2 => <SidebarNavItem key={item_2.label} icon={item_2.icon} label={item_2.label} href={item_2.href} active={item_2.active} onClick={onClose} dataGuide={item_2.dataGuide} layoutId="sidebar-workspace-active" collapsed={true} />)}</div>;
    $[151] = onClose;
    $[152] = workspaceItems;
    $[153] = t51;
  } else {
    t51 = $[153];
  }
  let t52;
  if ($[154] !== t48 || $[155] !== t51) {
    t52 = <div className="-mr-1 min-h-0 flex-1 overflow-y-auto pr-1">{t47}{t48}{t49}{t50}{t51}</div>;
    $[154] = t48;
    $[155] = t51;
    $[156] = t52;
  } else {
    t52 = $[156];
  }
  let t53;
  if ($[157] !== isAuthenticated) {
    t53 = isAuthenticated && <UserOrgSwitcher collapsed={true} />;
    $[157] = isAuthenticated;
    $[158] = t53;
  } else {
    t53 = $[158];
  }
  let t54;
  if ($[159] !== bottomSlot || $[160] !== t53) {
    t54 = <div className="mt-3 flex flex-col gap-2 pt-2">{bottomSlot}{t53}</div>;
    $[159] = bottomSlot;
    $[160] = t53;
    $[161] = t54;
  } else {
    t54 = $[161];
  }
  let t55;
  if ($[162] !== t46 || $[163] !== t52 || $[164] !== t54) {
    t55 = <TooltipProvider delayDuration={0}><div className="relative flex h-full flex-col justify-between px-1.5 py-1">{t36}{t46}{t52}{t54}</div></TooltipProvider>;
    $[162] = t46;
    $[163] = t52;
    $[164] = t54;
    $[165] = t55;
  } else {
    t55 = $[165];
  }
  let t56;
  if ($[166] !== collapsed || $[167] !== t35 || $[168] !== t55) {
    t56 = <Sidebar><SidebarBody collapsed={collapsed} className={t35}>{t55}</SidebarBody></Sidebar>;
    $[166] = collapsed;
    $[167] = t35;
    $[168] = t55;
    $[169] = t56;
  } else {
    t56 = $[169];
  }
  return t56;
}
function _temp4(href_0) {
  const link = NAVIGATION_LINKS.find(l => l.href === href_0);
  if (!link) {
    return null;
  }
  const Icon = link.icon;
  return <Icon className="size-5" />;
}
function _temp3(s) {
  return s.openSupport;
}
function _temp2(state) {
  return state.setOpen;
}